import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import SelectGroup from '../../../components/inputs/select/grouped/select-group';
import { FormProps } from '../../../shared/interfaces/form';
import CreateRubricaModal, { CreateRubricaPayload, RubricaTypeOption } from '../../modals/rubrica/create-rubrica-modal';
import { PendingRubricaOption } from '../../modals/rubrica/rubrica-modal';

interface FormSelectGroupProps {
  item: FormProps;
  index: number;
  baseId: string;
  errors: any;
  control: any;
  isDisabled: boolean;
  setValue: (name: string, value: any) => void;
  getValues: (name?: string | string[]) => any;
  rubricaTypes?: RubricaTypeOption[];
  pendingRubricas?: PendingRubricaOption[];
  onCreatePendingRubrica?: (payload: CreateRubricaPayload) => PendingRubricaOption;
}

const FormSelectGroup = ({
  item,
  index,
  baseId,
  errors,
  control,
  isDisabled,
  setValue,
  getValues,
  rubricaTypes = [],
  pendingRubricas = [],
  onCreatePendingRubrica,
}: FormSelectGroupProps) => {
  const [isDisabledSelect, setIsDisabledSelect] = React.useState(false);
  const [createState, setCreateState] = useState({
    open: false,
    initialName: '',
  });

  const supportsInlineRubricaCreation = item.name === 'idRubrica' && Boolean(onCreatePendingRubrica);

  const availableOptions = useMemo(() => {
    if (!supportsInlineRubricaCreation) {
      return item.options?.data || [];
    }

    const pendingOptions = pendingRubricas.map((pending) => ({
      id: pending.tempId,
      value: pending.tempId,
      label: pending.name,
      name: pending.name,
      group: pending.typeName || 'Novas',
    }));

    return [...(item.options?.data || []), ...pendingOptions];
  }, [item.options?.data, pendingRubricas, supportsInlineRubricaCreation]);
  
  const handleCustomChange = (value: any, onChange: any) => {
    onChange(value);
    
    // Aqui você pode adicionar lógica customizada baseada no item.name
    // Similar ao handleCustomChange do FormInput
    if (item.name === 'category') {
      // Exemplo de lógica customizada
    }
  };

  useEffect(() => {
    const page = window.location.hash.split('/')[1];

    if(page.toLocaleLowerCase().includes('list')) {
      setIsDisabledSelect(true);
    }
  }, []);

  const handleRequestCreateRubrica = useCallback((name: string) => {
    if (!supportsInlineRubricaCreation) return;
    setCreateState({
      open: true,
      initialName: name,
    });
  }, [supportsInlineRubricaCreation]);

  const handleCloseCreateModal = useCallback(() => {
    setCreateState({
      open: false,
      initialName: '',
    });
  }, []);

  const handleConfirmCreateRubrica = useCallback((payload: CreateRubricaPayload) => {
    if (!onCreatePendingRubrica) return;

    const pending = onCreatePendingRubrica(payload);
    setValue(item.name, pending.tempId);
    handleCloseCreateModal();
  }, [handleCloseCreateModal, item.name, onCreatePendingRubrica, setValue]);

  return (
    <>
      <Controller
        key={`select-group-${index}-${baseId}`}
        name={item.name}
        control={control}
        defaultValue={item.value || ''}
        rules={item.rules}
        render={({ field }) => (
          <div className={`form-input-wrapper ${item.fullWidth ? 'full-width' : ''}`}>
            <SelectGroup 
              data={availableOptions}
              placeholder={item.placeholder}
              error={errors[item.name]?.message}
              width={item.width}
              height={item.height}
              disabled={item.disabled || isDisabledSelect || isDisabled}
              value={field.value}
              onChange={(value: any) => handleCustomChange(value, field.onChange)}
              onCreate={supportsInlineRubricaCreation ? handleRequestCreateRubrica : undefined}
              createLabel={supportsInlineRubricaCreation
                ? (name) => `Adicionar nova rubrica "${name}"`
                : undefined}
            />
          </div>
        )}
      />

      {supportsInlineRubricaCreation && (
        <CreateRubricaModal
          isOpen={createState.open}
          onClose={handleCloseCreateModal}
          onConfirm={handleConfirmCreateRubrica}
          types={rubricaTypes}
          initialName={createState.initialName}
          descriptionText="A rubrica será criada quando a movimentação financeira for salva."
        />
      )}
    </>
  );
};

export default FormSelectGroup;
